import type { Chat, ChatBlock } from '@/api/ai/aiType';
import type { NodeOptions } from '@/api/distribution/distributionType';
import type { Experiment } from '@/api/experiment/experimentType';
import type { MindmapVideo } from '@/api/resource/videoApi';
import type { User } from '@/api/user/userType';
import type { SubjectId } from '@/components/subject/configs.ts';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { AI_SOURCE_MAIN } from '@/api/ai/sources';
import { fetchMindmapVideos } from '@/api/resource/videoApi';
import { subjectConfig } from '@/components/subject/configs.ts';

const DISTRIBUTION_STORE_VERSION = 4;

const useUserStore = defineStore('userStore', () => {
  const user = ref<User | null>(null);
  const visitorId = ref<string | null>(null);
  const favoriteExperiments = ref<Experiment[]>([]);
  function isFavorite(expId: string): boolean {
    return favoriteExperiments.value.some(exp => exp.expId === expId);
  }
  return {
    user,
    favoriteExperiments,
    isFavorite,
    visitorId,
  };
}, {
  persist: true,
});

const useAiStore = defineStore('aiStore', () => {
  const currentChat = ref<Chat | null>(null);
  // 按 source 分组的对话列表，各模块独立缓存
  const chatListMap = ref<Record<string, Chat[]>>({});
  const isLoadingHistory = ref(false);
  // 记录哪些 source 已经加载过
  const loadedSources = ref<string[]>([]);

  // 获取指定 source 的 chatList
  function getChatListBySource(source: string): Chat[] {
    if (!chatListMap.value[source]) {
      chatListMap.value[source] = [];
    }
    return chatListMap.value[source];
  }

  // 当前模块的 source，由 AiPanel 设置
  const currentSource = ref<string>(AI_SOURCE_MAIN);

  // 兼容旧代码：当前 source 对应的 chatList
  const chatList = computed({
    get: () => getChatListBySource(currentSource.value),
    set: (val: Chat[]) => {
      chatListMap.value[currentSource.value] = val;
    },
  });

  // 从服务器加载会话列表（按 source 隔离）
  async function loadConversationsFromServer(source?: string) {
    const targetSource = source || AI_SOURCE_MAIN;

    // 已经加载过该 source 的历史，不重复请求
    if (loadedSources.value.includes(targetSource))
      return;

    isLoadingHistory.value = true;
    try {
      const { getConversationsListApi } = await import('@/api/ai/aiApi');
      const response = await getConversationsListApi(targetSource);

      const serverChats: Chat[] = response.conversations.map(conv => ({
        chatBlocks: [],
        chatId: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        chatTitle: conv.title,
        lastChatTime: new Date(conv.updated_at).toLocaleString(),
        conversationId: conv.id,
        source: conv.source,
        subject: conv.subject,
        isLoaded: false,
      }));

      chatListMap.value[targetSource] = serverChats;
      loadedSources.value.push(targetSource);

      console.log(`[useAiStore] Loaded ${serverChats.length} conversations for source="${targetSource}"`);
    }
    catch (error) {
      console.error('[useAiStore] Failed to load conversations from server:', error);
    }
    finally {
      isLoadingHistory.value = false;
    }
  }

  // 加载会话详细消息
  async function loadConversationMessages(chat: Chat) {
    if (!chat.conversationId || chat.isLoaded)
      return;

    try {
      const { getConversationApi } = await import('@/api/ai/aiApi');
      const response = await getConversationApi(chat.conversationId);

      // 转换消息格式
      const chatBlocks: ChatBlock[] = [];
      response.messages.forEach((msg) => {
        // 只处理用户和助手消息
        if (msg.messageType === 'user' || msg.messageType === 'assistant') {
          chatBlocks.push({
            role: msg.messageType === 'assistant' ? 'ai' : 'user',
            messageId: msg.id,
            attachments: msg.attachments?.map(att => ({
              id: att.id,
              type: att.attachmentType === 'image' ? 'image' : 'file',
              name: att.filename,
              size: att.fileSize,
              url: att.fileUrl,
              previewUrl: att.attachmentType === 'image' ? att.fileUrl : undefined,
              summaryPreview: att.summaryPreview,
              parseStatus: att.parseStatus,
            })),
            data: [{
              type: 'text',
              text: msg.content,
            }],
            blockId: msg.id,
            isDeleted: msg.isDeleted,
            editedAt: msg.editedAt,
          });
        }
      });

      chat.chatBlocks = chatBlocks;
      chat.isLoaded = true;

      console.log(`[useAiStore] Loaded ${chatBlocks.length} messages for conversation ${chat.conversationId}`);
    }
    catch (error) {
      console.error('[useAiStore] Failed to load conversation messages:', error);
    }
  }

  return {
    currentChat,
    chatList,
    chatListMap,
    currentSource,
    isLoadingHistory,
    loadedSources,
    getChatListBySource,
    loadConversationsFromServer,
    loadConversationMessages,
  };
}, {
  persist: true,
});

const useConfigStore = defineStore('configStore', () => {
  const darkMode = ref<boolean>(false);
  const targetNodeId = ref<string | null>(null);
  const currentSubjectId = ref<SubjectId | null>(null);
  const dynamicSubject = ref<{
    id: string;
    id_: string;
    name: string;
    subjectName: string;
    description: string;
    color: string;
    teacher: string;
    icon: any;
    isCustomCourse: boolean;
  } | null>(null);
  const currentSubject = computed(() => {
    if (dynamicSubject.value)
      return dynamicSubject.value;
    return currentSubjectId.value ? subjectConfig[currentSubjectId.value] : null;
  });
  const isMoving = ref(false);
  const confirmedAiPolicy = ref(false);
  // Experiment like metadata (set by ExperimentBoard when an experiment with likes is opened)
  const currentExperimentLikeEnabled = ref<boolean>(false);
  const currentExperimentOwnerName = ref<string | null>(null);
  const currentExperimentContributors = ref<string | null>(null);
  const currentExperimentSchool = ref<string | null>(null);
  const currentExperimentLikeCount = ref<number | null>(null);
  const currentExperimentLikeId = ref<number | null>(null);
  const currentExperimentLiked = ref<boolean>(false);

  function updateTheme(subjectId: SubjectId | null) {
    const el = document.documentElement;
    const themeIds: SubjectId[] = [
      'probability',
      'probability-bilingual',
      'advanced-math-1',
      'advanced-math-2',
      'linear-algebra',
      'statistics',
      'number-theory',
      'set-theory',
      'solid-geometry',
      'analytic-geometry',
      'trigonometry',
      'plane-vectors',
      'functions',
      'logical-reasoning',
      'sequences-series',
      'derivatives',
      'probability-statistics',
      'risk-theory',
      'linear-algebra-en',
    ];
    for (const subject of themeIds) {
      el.classList.remove(`theme-${subject}`);
    }
    if (subjectId === 'probability' || subjectId === null)
      el.classList.add('theme-probability');
    else if (subjectId === 'probability-bilingual')
      el.classList.add('theme-probability-bilingual');
    else if (subjectId === 'advanced-math-1')
      el.classList.add('theme-advanced-math-1');
    else if (subjectId === 'advanced-math-2')
      el.classList.add('theme-advanced-math-2');
    else if (subjectId === 'linear-algebra')
      el.classList.add('theme-linear-algebra');
    else if (subjectId === 'statistics')
      el.classList.add('theme-statistics');
    else if (subjectId === 'number-theory')
      el.classList.add('theme-number-theory');
    else if (subjectId === 'set-theory')
      el.classList.add('theme-set-theory');
    else if (subjectId === 'solid-geometry')
      el.classList.add('theme-solid-geometry');
    else if (subjectId === 'analytic-geometry')
      el.classList.add('theme-analytic-geometry');
    else if (subjectId === 'trigonometry')
      el.classList.add('theme-trigonometry');
    else if (subjectId === 'plane-vectors')
      el.classList.add('theme-plane-vectors');
    else if (subjectId === 'functions')
      el.classList.add('theme-functions');
    else if (subjectId === 'logical-reasoning')
      el.classList.add('theme-logical-reasoning');
    else if (subjectId === 'sequences-series')
      el.classList.add('theme-sequences-series');
    else if (subjectId === 'derivatives')
      el.classList.add('theme-derivatives');
    else if (subjectId === 'probability-statistics')
      el.classList.add('theme-probability-statistics');
    else if (subjectId === 'risk-theory')
      el.classList.add('theme-risk-theory');
    else if (subjectId === 'linear-algebra-en')
      el.classList.add('theme-linear-algebra-en');
  }

  watch([currentSubjectId, dynamicSubject], () => {
    updateTheme(currentSubjectId.value);
  }, { immediate: true });

  return {
    confirmedAiPolicy,
    darkMode,
    targetNodeId,
    isMoving,
    currentSubject,
    currentSubjectId,
    dynamicSubject,
    currentExperimentLikeEnabled,
    currentExperimentOwnerName,
    currentExperimentContributors,
    currentExperimentSchool,
    currentExperimentLikeCount,
    currentExperimentLikeId,
    currentExperimentLiked,
  };
}, {
  persist: { omit: ['dynamicSubject'] },
});

const useDistributionStore = defineStore('distributionStore', () => {
  const nodeData = ref<NodeOptions[]>([
    {
      label: 'Zipf(α,n)',
      position: { x: 0, y: 0 },
      chineseTranslation: 'Zipf 分布 (α, n)',
      pmf: '\n$$\nf(x) = \\frac{1}{x^\\alpha \\sum\\limits_{i=1}^n \\left(\\frac{1}{i}\\right)^\\alpha}, \\quad x = 1, 2, \\ldots, n\n$$\n',
      imgPath: '/distribution/zipf.png',
    },
    {
      label: `Discrete uniform(a,b)\n R,V`,
      position: { x: 300, y: 0 },
      chineseTranslation: '离散均匀分布(a, b)\n R, V',
      pmf: '\n$$\nf(x) = \\frac{1}{b - a + 1}, \\quad x = a, a + 1, \\ldots, b\n$$\n',
      imgPath: '/distribution/discrete_uniform.png',
    },
    {
      label: 'Rectangular(n)\nV',
      position: { x: 600, y: 0 },
      chineseTranslation: '矩形分布(n)\nV',
      pmf: '\n$$\nf(x) = \\frac{1}{n + 1}, \\quad x = 0, 1, 2, \\ldots, n\n$$\n',
      imgPath: '/distribution/rectangular.png',
    },
    {
      label: 'Beta-binomial\n(a,b,n)',
      position: { x: 900, y: 0 },
      chineseTranslation: '贝塔二项分布\n(a, b, n)',
      pmf: '\n$$\nf(x) = \\frac{\\Gamma(x + a)\\, \\Gamma(n - x + b)\\, \\Gamma(a + b)\\, \\Gamma(n + 2)}{(n + 1)\\, \\Gamma(a + b + n)\\, \\Gamma(a)\\, \\Gamma(b)\\, \\Gamma(x + 1)\\, \\Gamma(n - x + 1)}, \\quad x = 0, 1, \\ldots, n\n$$\n',
      imgPath: '/distribution/beta_binomial.png',
    },
    {
      label: 'Negative hypergeometric\n(n1,n2,n3)',
      position: { x: 1250, y: 0 },
      chineseTranslation: '负超几何分布\n(n1, n2, n3)',
      pmf: '\n$$\nf(x) = \\frac{\\binom{n_1 + x - 1}{x} \\binom{n_3 - n_1 + n_2 - x - 1}{n_2 - x}}{\\binom{n_3 + n_2 - 1}{n_2}}, \\quad x = 0, 1, \\ldots, n_2\n$$\n',
      imgPath: '/distribution/negative_hypergeometric.png',
    },
    {
      label: 'Zeta(α)',
      position: { x: 0, y: 100 },
      chineseTranslation: 'Zeta分布(α)',
      pmf: '\n$$\nf(x) = \\frac{1}{x^\\alpha \\sum\\limits_{i=1}^{\\infty} \\left(\\frac{1}{i}\\right)^\\alpha}, \\quad x = 0, 1, 2, \\ldots\n$$\n',
      imgPath: '/distribution/zeta.png',
    },
    {
      label: 'Logarithm(c)',
      position: { x: 150, y: 200 },
      chineseTranslation: '对数分布(c)',
      pmf: '\n$$\nf(x) = -\\frac{(1 - c)^x}{x \\ln c}, \\quad x = 1, 2, \\ldots\n$$\n',
      imgPath: '/distribution/logarithm.png',
    },
    {
      label: 'Power series(c,A(c))',
      position: { x: 300, y: 100 },
      chineseTranslation: '幂级数分布(c, A(c))',
      pmf: '\n$$\nf(x) = \\frac{a_x c^x}{A(c)}, \\quad x = 0, 1, 2, \\ldots\n$$\n',
      imgPath: '/distribution/power_series.png',
    },
    {
      label: 'Poisson(μ)\nC',
      position: { x: 450, y: 200 },
      chineseTranslation: '泊松分布(μ)\nC',
      pmf: '\n$$\nf(x) = \\frac{\\mu^x e^{-\\mu}}{x!}, \\quad x = 0, 1, 2, \\ldots\n$$\n',
      expId: 'poisson-distribution',
      description: '**应用案例**: 网络流量中的数据包到达率\n\n- **背景**: 在网络工程中，数据包的到达通常被建模为泊松过程。\n- **研究目的**: 分析特定时间段内到达的数据包数量，以优化网络资源配置。\n- **方法**: 收集网络流量数据，计算在单位时间内到达的数据包数量，并使用泊松分布进行拟合，评估网络的负载和延迟。',
      imgPath: '/distribution/poisson.png',
    },
    {
      label: 'Hypergeometric\n(n1,n2,n3)',
      position: { x: 1300, y: 120 },
      chineseTranslation: '超几何分布\n(n1, n2, n3)',
      pmf: '\n$$\nf(x) = \\frac{\\binom{n_1}{x} \\binom{n_3 - n_1}{n_2 - x}}{\\binom{n_3}{n_2}}, \\quad x = \\max\\{0, n_1 + n_2 - n_3\\}, \\ldots, \\min\\{n_1, n_2\\}\n$$\n',
      description: '**应用案例**: 电子产品抽样检验\n\n- **背景**: 在生产过程中，质检人员常常需要从批量产品中抽样检验，以评估整体质量。超几何分布适用于这种不放回抽样的情况。\n- **研究目的**: 评估在抽样中检出不合格品的概率，以优化质检流程。\n- **方法**: 设定批次中合格与不合格产品的数量，进行不放回抽样，从中计算不合格品被检出的概率。',
      imgPath: '/distribution/hypergeometric.png',
    },
    {
      label: 'Beta-Pascal\n(n,a,b)',
      position: { x: 250, y: 500 },
      chineseTranslation: '贝塔-帕斯卡分布\n(n, a, b)',
      pmf: '\n$$\nf(x) = \\frac{\\binom{n - 1 + x}{x} B(n + a, b + x)}{B(a, b)}, \\quad x = 0, 1, \\ldots\n$$\n',
      imgPath: '/distribution/beta_pascal.png',
    },
    {
      label: 'Gamma-Poisson(α,β)',
      position: { x: 580, y: 300 },
      chineseTranslation: '伽马-泊松分布(α, β)',
      pmf: '\n$$\nf(x) = \\frac{\\Gamma(x + \\beta) \\alpha^x}{\\Gamma(\\beta)(1 + \\alpha)^{\\beta + x} x!}, \\quad x = 0, 1, 2, \\ldots\n$$\n',
      imgPath: '/distribution/gamma_poisson.png',
    },
    {
      label: 'Binomial(n,p)',
      position: { x: 800, y: 170 },
      chineseTranslation: '二项分布(n, p)',
      pmf: '\n$$\nf(x) = \\binom{n}{x} p^x (1 - p)^{n - x}, \\quad x = 0, 1, 2, \\ldots, n\n$$\n',
      expId: 'binomial-distribution',
      description: '**应用案例**: 电子产品的质量控制\n\n- **背景**: 在生产过程中，电子产品的缺陷率可以用二项分布来建模。\n- **研究目的**: 分析在生产批次中合格产品的比例，以确保产品质量。\n- **方法**: 对每批产品进行抽样检测，记录合格与不合格的产品数量。使用二项分布模型来估计不合格产品的概率，并进行统计检验，以确定生产过程是否符合质量标准。',
      imgPath: '/distribution/binomial.png',
    },
    {
      label: 'Bernoulli(p)\nM,P,X',
      position: { x: 1100, y: 250 },
      chineseTranslation: '伯努利分布(p)\nM, P, X',
      pmf: '\n$$\nf(x) = p^x (1 - p)^{1 - x}, \\quad x = 0, 1\n$$\n',
      imgPath: '/distribution/bernoulli.png',
    },
    {
      label: 'Geometric(p)\nF,M,V',
      position: { x: -300, y: 550 },
      chineseTranslation: '几何分布(p)\nF, M, V',
      pmf: '\n$$\nf(x) = p (1 - p)^x, \\quad x = 0, 1, 2, \\ldots\n$$\n',
      expId: 'geometric-distribution',
      imgPath: '/distribution/geometric.png',
    },
    {
      label: 'Pascal(n,p)\nCp',
      position: { x: 0, y: 400 },
      chineseTranslation: '帕斯卡分布(n, p)\nCp',
      pmf: '\n$$\nf(x) = \\binom{n - 1 + x}{x} p^n (1 - p)^x, \\quad x = 0, 1, 2, \\ldots\n$$\n',
      imgPath: '/distribution/pascal.png',
    },
    {
      label: 'Normal(μ,σ²)\nL',
      position: { x: 950, y: 600 },
      chineseTranslation: '正态分布(μ, σ²)\nL',
      pdf: '\n$$\nf(x) = \\frac{1}{\\sqrt{2\\pi\\sigma}} e^{-\\frac{(x - \\mu)^2}{2\\sigma^2}}, \\quad -\\infty < x < \\infty\n$$\n',
      expId: 'normal-distribution',
      imgPath: '/distribution/normal.png',
      description: '**应用案例**: 信号处理中的噪声建模\n\n- **背景**: 在无线通信和信号处理领域，信号在传输过程中往往会受到噪声的影响。许多类型的噪声（如高斯白噪声）可以用正态分布来建模。\n- **研究目的**: 分析信号在不同噪声条件下的传输质量。\n- **方法**: 通过实验收集不同信噪比（SNR）条件下的信号数据，利用正态分布模型对噪声进行分析，评估通信系统的性能。',
    },
    {
      label: 'Polya(n,p,β)',
      position: { x: 600, y: 80 },
      chineseTranslation: '波利亚分布(n, p, β)',
      imgPath: '/distribution/polya.png',
      pmf: '\n$$\nf(x) = \\binom{n}{x} \\frac{\\prod_{j=0}^{x-1} (p + j\\beta) \\prod_{k=0}^{n - x - 1} (1 - p + k\\beta)}{\\prod_{i=0}^{n - 1} (1 + i\\beta)}, \\quad x = 0, 1, 2, \\ldots, n\n$$\n',
    },
    {
      label: 'Gamma-normal(μ，α，β)',
      position: { x: 1100, y: 700 },
      chineseTranslation: '伽马正态分布(μ, α, β)',
      imgPath: '/distribution/gamma_normal.png',
      pdf: '$$\nf(\\mu, \\tau) = \\sqrt{\\frac{\\lambda \\tau}{2\\pi}} \\cdot \\frac{\\beta^\\alpha}{\\Gamma(\\alpha)} \\cdot \\tau^{\\alpha - \\frac{1}{2}} \\cdot \\exp\\left(-\\beta \\tau - \\frac{1}{2} \\lambda \\tau (\\mu - \\mu_0)^2\\right), \\quad \\mu \\in \\mathbb{R},\\ \\tau > 0\n$$',
    },
    {
      label: 'Discrete Weibull(p,β)\nV',
      position: { x: -400, y: 400 },
      chineseTranslation: '离散韦布尔分布(p, β)\nV',
      imgPath: '/distribution/discrete_weibull.png',
      pmf: '$$\nf(x) = (1 - p)^{x^\\beta} - (1 - p)^{(x + 1)^\\beta}, \\quad x = 0, 1, 2, \\ldots\n$$',
    },
    {
      label: 'Standard normal',
      position: { x: 580, y: 450 },
      chineseTranslation: '标准正态分布',
      pdf: '$$\nf(x) = \\frac{e^{-x^2 / 2}}{\\sqrt{2\\pi}}, \\quad -\\infty < x < \\infty\n$$\n',
      expId: 'normal-distribution',
      imgPath: '/distribution/standard_normal.png',
    },
    {
      label: 'Log normal(α，β)\nP',
      position: { x: 1100, y: 400 },
      chineseTranslation: '对数正态分布(α，β)\nP',
      imgPath: '/distribution/log_normal.png',
      pdf: '$$\nf(x) = \\frac{1}{x\\beta \\sqrt{2\\pi}} e^{-\\frac{1}{2} \\left( \\frac{\\ln(x/\\alpha)}{\\beta} \\right)^2}, \\quad x > 0\n$$',
      description: '**应用案例**: 无线通信中的阴影衰落建模\n\n- **背景**: 在无线通信中，阴影衰落（shadow fading）通常由建筑物、树木等障碍物引起，导致信号强度变化。这种变化常呈现出对数正态的特征。\n- **研究目的**: 模拟阴影衰落现象，以优化无线通信系统的设计和性能评估。\n- **特点**:\n - **对数正态分布**: 信号强度在对数尺度（通常以dB表示）上服从正态分布。\n - **均值和标准差**: 通过测量实际环境中的信号强度，估计分布的均值和标准差，以进行准确建模。\n - **影响因素**: 阴影衰落受到环境、用户速度、频率、天线高度等因素的影响。\n- **方法**: 测量信号强度，分析其对数分布特征，估计分布参数，利用对数正态分布模型对阴影衰落进行描述。',
    },
    {
      label: 'Noncentral beta(β，γ，δ)',
      position: { x: 1100, y: 500 },
      chineseTranslation: '非中心贝塔分布(β，γ，δ)',
      imgPath: '/distribution/noncentral_beta.png',
      pdf: '$$\nf(x) = \\sum_{i=0}^{\\infty} \\frac{\\Gamma(i + \\beta + \\gamma)}{\\Gamma(\\gamma)\\Gamma(i + \\beta)} \\left( \\frac{e^{-\\delta/2}}{i!} \\right) \\left( \\frac{\\delta}{2} \\right)^i x^{i + \\beta - 1} (1 - x)^{\\gamma - 1}, \\quad 0 < x < 1\n$$',
    },
    {
      label: 'Arctangent(λ,Φ)\nS,V',
      position: { x: 150, y: 600 },
      chineseTranslation: '反正切(λ, Φ)\nS, V',
      pdf: '$$\nf(x) = \\frac{\\lambda}{\\left( \\arctan(\\lambda \\Phi) + 1/2\\pi \\right) \\left(1 + \\lambda^2 (x - \\Phi)^2 \\right)}, \\quad x \\geq 0\n$$',
      imgPath: '/distribution/arctangent.png',
    },
    {
      label: 'Noncentral chi-square(n,δ)\nC',
      position: { x: 950, y: 750 },
      chineseTranslation: '非中心卡方分布(n, δ)\nC',
      pdf: '\n$$\nf(x) = \\frac{1}{2} e^{-(x + \\delta)/2} \\left( \\frac{x}{\\delta} \\right)^{(n/4 - 1/2)} I_{n/2 - 1}(\\sqrt{\\delta x})\n$$\n',
      imgPath: '/distribution/noncentral_chi2.png',
    },
    {
      label: 'Log gamma(α,β)',
      position: { x: 850, y: 800 },
      chineseTranslation: '对数伽马分布(α, β)',
      pdf: '$$\nf(x) = \\frac{e^{\\beta x} e^{-e^{x}/\\alpha}}{\\alpha^\\beta \\Gamma(\\beta)}, \\quad -\\infty < x < \\infty\n$$\n',
      imgPath: '/distribution/log_gamma.png',
    },
    {
      label: 'Generalized gamma(α，β,γ)',
      position: { x: 800, y: 250 },
      chineseTranslation: '广义伽马分布(α，β, γ)',
      pdf: '\n$$\nf(x) = \\frac{\\beta}{\\Gamma(\\alpha / \\gamma)} x^{\\alpha - 1} e^{-(x / \\gamma)^\\beta}, \\quad x > 0\n$$\n',
      imgPath: '/distribution/generalized_gamma.png',
    },
    {
      label: 'Hyperbolic-secant\nV',
      position: { x: 0, y: 800 },
      chineseTranslation: '双曲割线分布\nV',
      pdf: '\n$$\nf(x) = \\frac{1}{2} \\text{sech}\\left(\\frac{\\pi x}{2}\\right)\n$$\n',
      imgPath: '/distribution/hyperbolic_secant.png',
    },
    {
      label: 'Inverse Gaussian(λ,μ)\nLμi/(μi²ai)',
      position: { x: 500, y: 350 },
      chineseTranslation: '反高斯分布(λ, μ)\nLμi/(μi²ai)',
      pdf: '\n$$\nf(x) = \\left( \\frac{\\lambda}{2\\pi x^3} \\right)^{1/2} e^{-\\frac{\\lambda (x - \\mu)^2}{2 \\mu^2 x}}\n$$\n',
      imgPath: '/distribution/inverse_gaussian.png',
    },
    // Chi 和 Chi-square 重复!!
    {
      label: 'Chi(n)',
      position: { x: 650, y: 800 },
      chineseTranslation: '卡方分布(n)',
      pdf: '\n$$\nf(x) = \\frac{1}{2^{n/2} \\Gamma(n/2)} x^{n/2 - 1} e^{-x / 2}, \\quad x > 0\n$$\n',
      imgPath: '/distribution/chi_square.png',
    },
    {
      label: 'Inverted gamma(α,β)',
      position: { x: 950, y: 1100 },
      chineseTranslation: '反伽马分布(α, β)',
      pdf: '\n$$\nf(x) = \\frac{\\beta^\\alpha}{\\Gamma(\\alpha)} x^{-\\alpha - 1} e^{-\\beta / x}, \\quad x > 0\n$$\n',
      imgPath: '/distribution/inverted_gamma.png',
    },
    {
      label: 'Gamma(α,β)\nCα,S',
      position: { x: 950, y: 950 },
      chineseTranslation: '伽马分布(α, β)\nCα, S',
      pdf: '\n$$\nf(x) = \\frac{\\beta^\\alpha}{\\Gamma(\\alpha)} x^{\\alpha - 1} e^{-\\beta x}, \\quad x > 0\n$$\n',
      imgPath: '/distribution/gamma.png',
      description: '**应用案例**: 数据传输延迟建模\n\n- **背景**: 在网络通信中，数据包传输的延迟可能受到多种因素的影响，常常呈现出复杂的分布特征。\n- **研究目的**: 对数据传输延迟进行建模，以优化网络性能和资源分配。\n- **方法**: 收集不同条件下的传输延迟数据，使用伽马分布进行拟合，对比不同网络条件下的延迟分布，评估网络质量。',
    },
    {
      label: 'Beta(β,γ)',
      position: { x: 1150, y: 800 },
      chineseTranslation: '贝塔分布(β, γ)',
      pdf: '\n$$\nf(x) = \\frac{\\Gamma(\\beta + \\gamma)}{\\Gamma(\\beta) \\Gamma(\\gamma)} x^{\\beta - 1} (1 - x)^{\\gamma - 1}, \\quad 0 < x < 1\n$$\n',
      imgPath: '/distribution/beta.png',
      description: '**应用案例**: 概率预测与决策支持\n\n- **背景**: Beta分布常用于建模概率和比例，适合在[0, 1]区间内的随机变量建模。\n- **研究目的**: 在项目管理和决策支持系统中，评估某一事件成功的概率。\n- **方法**: 收集历史数据（如项目成功与失败的次数），利用Beta分布进行参数估计，计算成功概率的后验分布。',
    },
    {
      label: 'Cauchy(a,α)\nC,I,S,V',
      position: { x: -100, y: 500 },
      chineseTranslation: '柯西分布(a, α)\nC, I, S, V',
      pdf: '\n$$\nf(x) = \\frac{1}{\\pi \\alpha \\left[ 1 + \\left( \\frac{x - a}{\\alpha} \\right)^2 \\right]}\n$$\n',
      imgPath: '/distribution/cauchy.png',
    },
    {
      label: 'Standard Waid(λ)\nS',
      position: { x: 500, y: 600 },
      chineseTranslation: '标准Waid分布(λ)\nS',

      imgPath: '/distribution/standard_waid.png',
      // Assuming it's a typo for Weibull
    },
    {
      label: 'Chi-square(n)\nC',
      position: { x: 450, y: 900 },
      chineseTranslation: '卡方分布(n)\nC',
      pdf: '\n$$\nf(x) = \\frac{1}{2^{n/2} \\Gamma(n/2)} x^{n/2 - 1} e^{-x / 2}, \\quad x > 0\n$$\n',
      imgPath: '/distribution/chi_square.png',
    },
    {
      label: 'Inverted beta(β,γ)',
      position: { x: 1150, y: 1100 },
      chineseTranslation: '反贝塔分布(β, γ)',

      imgPath: '/distribution/inverted_beta.png',

    },
    {
      label: 'Arcsin\nV',
      position: { x: 1250, y: 900 },
      chineseTranslation: '反正弦分布\nV',

      imgPath: '/distribution/arcsin.png',
    },
    {
      label: 'Standard Cauchy\nI,S,V',
      position: { x: -300, y: 700 },
      chineseTranslation: '标准柯西分布\nI, S, V',
      pdf: '\n$$\nf(x) = \\frac{1}{\\pi (1 + x^2)}\n$$\n',
      imgPath: '/distribution/standard_cauchy.png',
    },
    {
      label: 't(n)',
      position: { x: -450, y: 620 },
      chineseTranslation: 't 分布(n)',
      pdf: '\n$$\nf(x) = \\frac{\\Gamma\\left(\\frac{n + 1}{2}\\right)}{\\sqrt{n \\pi} \\, \\Gamma\\left(\\frac{n}{2}\\right)} \\left(1 + \\frac{x^2}{n} \\right)^{-(n + 1)/2}\n$$\n',
      imgPath: '/distribution/t.png',
      description: '**应用案例**: 小样本统计分析\n\n- **背景**: 在电子信息领域的实验研究中，常常面临样本量较小的情况，这时使用t分布可以得到更准确的假设检验结果。\n- **研究目的**: 对实验结果进行显著性检验，判断新算法与传统算法的性能差异。\n- **方法**: 收集不同算法在相同数据集上的实验结果，使用t检验分析两组样本均值是否存在显著差异。',
    },
    {
      label: 'Hypoexponential(vector α)\nC',
      position: { x: 550, y: 1000 },
      chineseTranslation: '超指数分布(向量 α)\nC',

      imgPath: '/distribution/hypoexponential.png',
    },
    {
      label: 'Erlang(α,n)\nS',
      position: { x: 300, y: 500 },
      chineseTranslation: '厄朗分布(α, n)\nS',
      pdf: '\n$$\nf(x) = \\frac{\\beta^{k} x^{k-1} e^{-\\beta x}}{(k-1)!}, \\quad k = n, \\beta = \\alpha\n$$\n',
      imgPath: '/distribution/erlang.png',
    },
    {
      label: 'Makeham(δ,κ,γ)',
      position: { x: -350, y: 100 },
      chineseTranslation: 'Makeham 分布(δ, κ, γ)',

      imgPath: '/distribution/makeham.png',
    },
    {
      label: 'Noncentral t(n,δ)',
      position: { x: -350, y: 100 },
      chineseTranslation: '非中心 t 分布(n, δ)',
      // pdf: null,
      imgPath: '/distribution/noncentral_t.png',
    },
    {
      label: 'F(n1,n2)',
      position: { x: -350, y: 100 },
      chineseTranslation: 'F 分布(n1, n2)',
      pdf: '\n$$\nf(x) = \\frac{\\sqrt{\\frac{(n1 x)^{n1} n2^{n2}}{(n1 x + n2)^{n1 + n2}}}}{x B(n1/2, n2/2)}\n$$\n',
      imgPath: '/distribution/f.png',
    },
    {
      label: 'Exponential(α)\nF,M,S,V',
      position: { x: -350, y: 100 },
      chineseTranslation: '指数分布(α)\nF, M, S, V',
      pdf: '\n$$\nf(x) = \\alpha e^{-\\alpha x}, \\quad x \\ge 0\n$$\n',
      imgPath: '/distribution/exponential.png',
      expId: 'exponential-distribution',
      description: '**应用案例**: 通信故障的寿命分析\n\n- **背景**: 在电子设备和网络组件中，故障发生时间常常服从指数分布。\n- **研究目的**: 评估设备的平均故障时间，以提前进行维护和更换。\n- **方法**: 收集设备故障时间数据，利用指数分布模型计算设备的可靠性和故障率。',
    },
    {
      label: 'Logistic-exponential(α,β)\nS,V',
      position: { x: -350, y: 100 },
      chineseTranslation: '逻辑-指数分布(α, β)\nS, V',
      pdf: '\n$$\nf(x;\\alpha,\\beta) = \\frac{\\beta}{\\alpha}e^{\\frac{x}{\\alpha}} · \\frac{1}{(1 + e^{\\frac{x}{\\alpha}})^{\\beta+1}}, \\quad x \\in \\mathbb{R}\n$$\n',
      imgPath: '/distribution/logistic_exponential_distribution.png',
    },
    {
      label: 'Gompertz(δ,κ)\nV',
      position: { x: -350, y: 100 },
      chineseTranslation: 'Gompertz分布(δ, κ)\nV',
      pdf: '\n$$\nf(x;\\delta,\\kappa) = \\delta e^{\\kappa x} · e^{-\\delta \\frac{e^{\\kappa x}-1}{\\kappa}}, \\quad x \\ge 0, \\quad \\delta,\\kappa \\gt 0\n$$\n',
      imgPath: '/distribution/Gompertz_distribution.png',
    },
    {
      label: 'Exponential Power(λ,κ)',
      position: { x: -350, y: 100 },
      chineseTranslation: '指数幂分布(λ, κ)',
      pdf: '\n$$\nf(x;\\lambda,\\kappa) = \\frac{\\kappa}{2\\lambda \\Gamma(\\frac{1}{\\kappa})} · e^{-|\\frac{x}{\\lambda}|^\\kappa}, \\quad x\\in \\mathbb{R}, \\quad \\alpha,\\beta \\gt 0\n$$\n',
      imgPath: '/distribution/exponential_power_distribution.png',
    },
    {
      label: 'Doubly noncentral t(n,δ,γ)',
      position: { x: -350, y: 100 },
      chineseTranslation: '双重非中心 t 分布(n, δ, γ)',
      pdf: '\n$$\nf(x;n,\\delta,\\gamma) = \\frac{2e^{-\\frac{\\delta^2+\\gamma^2}{2}}}{\\sqrt{nπ}\\Gamma(\\frac{n}{2})}\\Sigma^\\infty_{k=0}\\Sigma^\\infty_{m=0}\\frac{(\\delta^{2k}\\gamma^{2m})}{k!m!2^{k+m}} · \\frac{|x|^{2m+1}(x^2+n)^{-(k+m+\\frac{n+1}{2})}}{\\Gamma(k+m+\\frac{n+1}{2})}, \\\\\\ x\\in \\mathbb{R}, \\quad \\delta,\\gamma \\in \\mathbb{R}, \\quad n \\gt 0\n$$\n',
      imgPath: '/distribution/doubly_noncentral_t_distribution.png',
    },
    {
      label: 'Hyperexponential',
      position: { x: -350, y: 100 },
      chineseTranslation: '超指数分布',
      pdf: '\n$$\nf(x) = \\Sigma^k_{i=1}p_i\\lambda_i e^{-\\lambda_i x}, \\quad x\\ge 0, \\quad \\lambda_i\\ge 0, \\quad \\Sigma^k_{i=1}p_i = 1 \\quad(i = 1,2,\\ldots,k)\n$$\n',
      imgPath: '/distribution/hyperexponential_distribution.png',
    },
    {
      label: 'Muth(κ)',
      position: { x: -350, y: 100 },
      chineseTranslation: 'Muth 分布(κ)',
      pdf: '\n$$\nf(t) = \\frac{\\kappa}{\\theta}(\\frac{t}{\\theta})^{\\kappa-1}e^{-(\\frac{t}{\\theta})^\\kappa}, \\quad t\\ge 0, \\quad \\theta,\\kappa\\gt 0\n$$\n',
      imgPath: '/distribution/Muth_distribution.png',
    },
    {
      label: 'Error(a,b,c)\nS',
      position: { x: -350, y: 100 },
      chineseTranslation: '误差分布(a, b, c)\nS',
      pdf: '\n$$\nf(x) = \\frac{e^{\\frac{(-\\frac{|x-a|}{b})^{\\frac{2}{c}}}{2}}}{b(2^{\\frac{c}{2}+1})\\Gamma(\\frac{c}{2} + 1)}, \\quad x\\in \\mathbb{R}\n$$\n',
      imgPath: '/distribution/Error_distribution.png',
    },
    {
      label: 'Standard uniform\nV',
      position: { x: -350, y: 100 },
      chineseTranslation: '标准均匀分布\nV',
      pdf: '\n$$\nf(x) = \\begin{cases} 1 & \\text{if } 0 \\le x \\le 1, \\\\ 0 & \\text{otherwise.} \\end{cases}\n$$\n',
      imgPath: '/distribution/standard_norm.png',
      expId: 'uniform-distribution',
    },
    {
      label: 'Minimax(β,γ)\nMβ,V',
      position: { x: -350, y: 100 },
      chineseTranslation: 'Minimax 分布(β, γ)\nMβ, V',
      pdf: '\n$$\nf(x) = \\beta\\gamma x^{\\beta-1}(1-x^\\beta)^{\\gamma-1}, \\quad 0 < x < 1.\n$$\n',
      imgPath: '/distribution/minimax_distribution.png',
    },
    {
      label: 'Noncentral F(n1,n2,δ)',
      position: { x: -350, y: 100 },
      chineseTranslation: '非中心 F 分布(n1, n2, δ)',
      pdf: '\n$$\nf(x;n1,n2,\\delta) = \\Sigma^\\infty_{k=0}\\frac{e^{-\\frac{\\delta}{2}}(\\frac{\\delta}{2})^k}{k!} · \\frac{\\Gamma(\\frac{n1+n2}{2}+k)}{\\Gamma(\\frac{n1}{2}+k)\\Gamma(\\frac{n2}{2})} · (\\frac{n1}{n2})^{\\frac{n1}{2}+k}x^{\\frac{n1}{2}+k-1}(1 + \\frac{n1}{n2}x)^{-\\frac{n1+n2}{2}-k} \\\\\\ x \\gt 0, \\quad n1,n2 \\gt 0, \\quad \\delta \\ge 0\n$$\n',
      imgPath: '/distribution/noncentral_F_distribution.png',

    },
    {
      label: 'IDB(δ,κ,γ)',
      position: { x: -350, y: 100 },
      chineseTranslation: 'IDB 分布(δ, κ, γ)',
      pdf: '\n$$\nf(x) = \\frac{((1+\\kappa x)\\delta x + \\gamma)e^{-\\frac{1}{2\\delta x^2}}}{(1 + \\kappa x)^{\\frac{\\gamma}{\\kappa+1}}}, \\quad x \\gt 0, \\quad \\delta \\gt 0, \\quad \\kappa \\gt 0 \\quad and \\quad \\gamma \\gt 0\n$$\n',
      imgPath: '/distribution/IDB_distribution.png',
    },
    {
      label: 'Laplace(α1,α2)\nV',
      position: { x: -350, y: 100 },
      chineseTranslation: '拉普拉斯分布(α1, α2)\nV',
      pdf: '\n$$\nf(x) = \\frac{1}{2\\alpha_2} e^{-\\frac{|x - \\alpha_1|}{\\alpha_2}}, \\quad x\\in \\mathbb{R}, \\quad \\alpha_1\\in \\mathbb{R}, \\quad \\alpha_2 \\gt 0\n$$\n',
      imgPath: '/distribution/laplace_distribution.png',
    },
    {
      label: 'Standard power(β)\nV,X',
      position: { x: -350, y: 100 },
      chineseTranslation: '标准幂分布(β)\nV, X',
      pdf: '\n$$\nf(x;\\beta) = \\begin{cases} \\beta x^{\\beta-1}, & x\\in {[0,1]} \\\\\\ 0, & \\text{otherwise.} \\end{cases}\n$$\n',
      imgPath: '/distribution/standard_power_distribution.png',
    },
    {
      label: 'Doubly noncentral F(n1,n2,δ,γ)',
      position: { x: -350, y: 100 },
      chineseTranslation: '双重非中心 F 分布(n1, n2, δ, γ)',
      pdf: '\n$$\nf(x;n1,n2,\\delta,\\gamma) = e^{-\\frac{\\delta+\\gamma}{2}} \\Sigma^\\infty_{i=0}\\Sigma^\\infty_{j=0} \\frac{(\\frac{\\delta}{2})^i(\\frac{\\gamma}{2})^j}{i!j!} · \\frac{\\Gamma(\\frac{n1+n2}{2}+i+j)}{\\Gamma(\\frac{n1}{2}+i)\\Gamma(\\frac{n2}{2}+j)} · (\\frac{n1}{n2})^{\\frac{n1}{2}+i} · \\frac{x^{\\frac{n1}{2}+i-1}}{(1+\\frac{n1}{n2}x)^{\\frac{n1+n2}{2}+i+j}}, \\\\\\ x \\gt 0, \\quad n1,n2 \\gt 0, \\quad \\delta,\\gamma \\ge 0\n$$\n',
      imgPath: '/distribution/doubly_noncentral_F_distribution.png',
    },
    {
      label: 'Rayleigh(α)\nM,S,V',
      position: { x: -350, y: 100 },
      chineseTranslation: '瑞利分布(α)\nM, S, V',
      pdf: '\n$$\nf(x) = \\frac{x}{\\alpha^2} e^{-x^2 / (2 \\alpha^2)}, \\quad x \\ge 0, \\quad \\alpha \\gt 0\n$$\n',
      imgPath: '/distribution/Rayleigh_distribution.png',
    },
    {
      label: 'Pareto(λ,κ)\nM,V',
      position: { x: -350, y: 100 },
      chineseTranslation: '帕累托分布(λ, κ)\nM, V',
      pdf: '\n$$\nf(x) = \\frac{\\lambda \\kappa^{\\lambda}}{x^{\\lambda + 1}}, \\quad x \\ge \\kappa\n$$\n',
      imgPath: '/distribution/Pareto_distribution.png',
    },
    {
      label: 'Standard trangular\nV',
      position: { x: -350, y: 100 },
      chineseTranslation: '标准三角分布\nV',
      pdf: '\n$$\nf(x) = \\begin{cases} 2x & 0 \\le x < 1, \\\\ 2(2 - x) & 1 \\le x \\le 2, \\\\ 0 & \\text{otherwise.} \\end{cases}\n$$\n',
      imgPath: '/distribution/standard_triangle_distribution.png',
    },
    {
      label: 'Power(α,β)\nS,V,Xα',
      position: { x: -350, y: 100 },
      chineseTranslation: '幂分布(α, β)\nS, V, Xα',
      pdf: '\n$$\nf(x) = \\frac{\\alpha \\beta^\\alpha}{x^{\\alpha + 1}} e^{-\\beta / x}, \\quad x > 0, \\quad \\alpha \\gt 0, \\quad \\alpha \\gt 0\n$$\n',
      imgPath: '/distribution/power_distribution.png',
    },
    {
      label: 'Weibull(α,β)\nMβ，S,V',
      position: { x: -350, y: 100 },
      chineseTranslation: '韦布尔分布(α, β)\nMβ, S, V',
      pdf: '\n$$\nf(x) = \\alpha \\beta x^{\\alpha - 1} e^{-(x / \\beta)^\\alpha}, \\quad x \\ge 0, \\quad \\alpha \\gt 0, \\quad \\beta \\gt 0\n$$\n',
      imgPath: '/distribution/weibull_distribution.png',
      description: '**应用案例**: 设备可靠性与寿命预测\n\n- **背景**: 在电子设备的研发与生产中，设备的故障模式和寿命常常表现出特定的分布特征，Weibull分布被广泛应用于可靠性工程。\n- **研究目的**: 通过分析设备的故障数据，预测其寿命并制定维护计划。\n- **方法**: 收集设备运行时间与故障数据，使用Weibull分布进行参数估计，分析其失效率和可靠性。',
    },
    {
      label: 'Log logistc(λ,κ)\nI,S,V',
      position: { x: -350, y: 100 },
      chineseTranslation: '对数逻辑分布(λ, κ)\nI, S, V',
      pdf: '\n$$\nf(x;\\lambda,\\kappa) = \\frac{\\kappa}{\\lambda} (\\frac{x}{\\lambda})^{\\kappa-1} · \\frac{1}{[1 + (\\frac{x}{\\lambda})^\\kappa]^2}, \\quad x \\gt 0, \\quad \\lambda \\gt 0, \\quad \\kappa \\gt 0\n$$\n',
      imgPath: '/distribution/log_logistic_distribution.png',
    },
    {
      label: 'Benford\nV',
      position: { x: -350, y: 100 },
      chineseTranslation: '本福特定律\nV',
      pdf: '\n$$\nP(D = d) = \\lg (1 + \\frac{1}{d}), \\quad d = 1,2,3,\\ldots,9. \n$$\n',
      imgPath: '/distribution/Benford_distribution.png',
    },
    {
      label: 'TSP(a,b,m,n)\nV',
      position: { x: -350, y: 100 },
      chineseTranslation: 'TSP(a, b, m, n)\nV',
      pdf: '\n$$\n\\begin{cases} \\frac{2(x-a)}{(b-a)(c-a)}, & a \\le x \\le c, \\\\\\ \\frac{2(b-x)}{(b-a)(b-c)}, & c \\lt x \\le b, \\\\\\ 0, \\text{otherwise.}. \\end{cases} \n$$\n',
      imgPath: '/distribution/TSP_distribution.png',
    },
    {
      label: 'Uniform(a,b)\nR,V',
      position: { x: -350, y: 100 },
      chineseTranslation: '均匀分布(a, b)\nR, V',
      pdf: '\n$$\nf(x) = \\frac{1}{b - a}, \\quad a \\le x \\le b, \\quad a \\neq b\n$$\n',
      imgPath: '/distribution/uniform_distribution.png',
      expId: 'uniform-distribution',
      description: '**应用案例**: 随机采样与仿真\n\n- **背景**: 在进行系统仿真或随机采样时，均匀分布常常用于生成随机数。\n- **研究目的**: 在仿真中随机生成事件发生的时间或位置，以评估系统性能。\n- **方法**: 利用均匀分布生成随机变量，例如在网络流量模拟中生成随机到达时间，评估系统在不同负载条件下的响应能力。',
    },
    {
      label: 'Extreme value(α,β)\nV,Mβ',
      position: { x: -350, y: 100 },
      chineseTranslation: '极值分布(α, β)\nV, Mβ',
      pdf: '\n$$\nf(x) = \\beta e^{-\\beta (x - \\alpha)} e^{-e^{-\\beta (x - \\alpha)}}, \\quad x \\in \\mathbb{R}\n$$\n',
      imgPath: '/distribution/extreme_distribution.png',
    },
    {
      label: 'Lomax(λ,κ)\nV',
      position: { x: -350, y: 100 },
      chineseTranslation: 'Lomax 分布(λ, κ)\nV',
      pdf: '\n$$\nf(x) = \\frac{\\lambda}{\\kappa} \\left(1 + \\frac{x}{\\kappa}\\right)^{-(\\lambda + 1)}, \\quad x \\gt 0, \\quad {\\lambda} \\gt 0, \\quad {\\kappa} \\gt 0\n$$\n',
      imgPath: '/distribution/lomax_distribution.png',
    },
    {
      label: 'von Mises(κ,μ)\nS',
      position: { x: -350, y: 100 },
      chineseTranslation: '冯·米塞斯分布(κ, μ)\nS',
      pdf: '$$\nf(x) = \\frac{e^{\\kappa \\cos(x - \\mu)}}{2\\pi I_0(\\kappa)}, \\quad 0 < x < 2\\pi\n$$\n',
    },
    {
      label: 'Generalized Pareto(δ,κ,γ)',
      position: { x: -350, y: 100 },
      chineseTranslation: '广义帕累托分布(δ, κ, γ)',
      pdf: '$$\nf(x) = \\left( \\gamma + \\frac{\\kappa}{x + \\delta} \\right)(1 + x / \\delta)^{-\\kappa} e^{-\\gamma x}, \\quad \\delta > 0;\\ \\gamma \\geq 0;\\ \\kappa \\geq -\\delta \\gamma\n$$\n',
    },
    {
      label: 'Logistic(κ,γ)\nS,V',
      position: { x: -350, y: 100 },
      chineseTranslation: '逻辑斯蒂分布(κ, γ)\nS, V',
      pdf: '$$\nf(x) = \\frac{\\lambda^\\kappa \\kappa e^{\\kappa x}}{\\left(1 + (\\lambda e^x)^\\kappa\\right)^2}, \\quad -\\infty < x < \\infty\n$$\n',
    },
    {
      label: 'Triangular(a,b,m)\nV',
      position: { x: -350, y: 100 },
      chineseTranslation: '三角分布(a, b, m)\nV',
      pdf: '$$\n'
        + 'f(x) = \n'
        + '\\begin{cases}\n'
        + '\\frac{2(x - a)}{(b - a)(m - a)}, & a < x < m \\\\\n'
        + '\\frac{2(b - x)}{(b - a)(b - m)}, & m \\leq x < b\n'
        + '\\end{cases}\n'
        + '$$\n',
    },
    {
      label: 'Kolmogorov-Smirnov(n)\nV(1-4)',
      position: { x: -350, y: 100 },
      chineseTranslation: 'Kolmogorov-Smirnov(n)\nV(1-4)',
      pdf: '$$\nf_{X_1, X_2}(x_1, x_2) = 1, \\quad 0 < x_1 < 1; \\ 0 < x_2 < 1\n$$',
    },
  ]);
  const version = ref(DISTRIBUTION_STORE_VERSION);
  return {
    nodeData,
    version,
  };
}, {
  persist: {
    key: 'distributionStore',
    storage: localStorage,
    beforeHydrate: () => {
      const stored = JSON.parse(localStorage.getItem('distributionStore') || '{}');
      if (stored.version !== DISTRIBUTION_STORE_VERSION) {
        localStorage.removeItem('distributionStore'); // 版本不一致，移除旧数据
        console.log('Distribution store version mismatch, resetting data.');
      }
    },
  },
});

const useMindmapVideoStore = defineStore('mindmapVideoStore', () => {
  const videos = ref<MindmapVideo[]>([]);
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<string | null>(null);

  function normalizeKey(value: string | undefined | null): string {
    if (!value)
      return '';
    return value.toLowerCase().replace(/[^a-z0-9\u4E00-\u9FA5]+/g, '');
  }

  const videosByName = computed(() => {
    return videos.value.reduce<Record<string, MindmapVideo[]>>((acc, video) => {
      const key = normalizeKey(video.knowledgePoint);
      if (!key)
        return acc;
      (acc[key] ??= []).push(video);
      return acc;
    }, {});
  });

  const videosByKnowledgeId = computed(() => {
    return videos.value.reduce<Record<string, MindmapVideo[]>>((acc, video) => {
      if (!video.knowledgeId)
        return acc;
      const key = String(video.knowledgeId);
      (acc[key] ??= []).push(video);
      return acc;
    }, {});
  });

  async function fetchVideos(force = false) {
    if (loading.value || (loaded.value && !force))
      return;

    loading.value = true;
    error.value = null;
    try {
      const result = await fetchMindmapVideos();
      videos.value = result.map(video => ({
        ...video,
        url: video.url.split('http://umathhub.com').pop() ?? video.url,
      }));
      loaded.value = true;
    }
    catch (err: any) {
      error.value = err?.message ?? '视频加载失败';
      throw err;
    }
    finally {
      loading.value = false;
    }
  }

  interface VideoLookupOptions {
    knowledgeId?: string | number | null;
    alternates?: Array<string | undefined | null>;
  }

  function getVideosForKnowledgePoint(name: string | undefined | null, options?: VideoLookupOptions): MindmapVideo[] {
    if (options?.knowledgeId) {
      const byId = videosByKnowledgeId.value[String(options.knowledgeId)];
      if (byId?.length)
        return byId;
    }

    const candidates = [name, ...(options?.alternates ?? [])]
      .map(item => normalizeKey(item))
      .filter(Boolean);

    for (const key of candidates) {
      const match = videosByName.value[key];
      if (match?.length)
        return match;
    }

    return [];
  }

  return {
    videos,
    loading,
    loaded,
    error,
    fetchVideos,
    getVideosForKnowledgePoint,
  };
});

export type {
  SubjectId,
};

export {
  useAiStore,
  useConfigStore,
  useDistributionStore,
  useMindmapVideoStore,
  useUserStore,
};
